import type { OnboardingStepContext } from "@/pages/onboarding/stepMachine/onboardingStepContext";
import type { OwnerPostCertViewSteps } from "@/pages/onboarding/ownerPostCertSteps";
import type { CertificateRequestStatus } from "@/services/certificateRequestsService";
import { isCertRequestInFlight } from "@/pages/onboarding/certificatePhase";

/**
 * The onboarding wizard's back-navigation and "can advance" logic, extracted
 * from `useOnboarding`'s inline `handleBackStep` reducer and `nextButtonDisabled`
 * expression as pure, unit-tested functions. `viewStep` stays an integer (the
 * public wire value), so these are keyed on the integer rather than a Step enum.
 * Behavior is frozen by the golden-master snapshots.
 *
 * (The forward `nextStep` action — async, side-effectful — is added alongside
 * these once its transcription lands.)
 */

export interface BackResult {
  /** The viewStep to navigate back to. */
  step: number;
  /** True on the CFE->CAE edge, where the caller also fires
   *  updateTenant({ onboardingCfeImportStepCompletedAt: null }) + loadTenant. */
  cfeReset?: boolean;
}

/** The slice of the step context `backStep` reads. */
export type BackContext = Pick<
  OnboardingStepContext,
  | "prevViewStep"
  | "isAccountantFlowOwner"
  | "isAccountantUser"
  | "isAlreadyIssuer"
  | "certBeforeDgi"
  | "onboardingSituationChoice"
  | "certificateAcquisitionChoice"
  | "ownerPostCertViewSteps"
  | "needsCfeImportStep"
  | "preCertOffset"
>;

/**
 * Pure form of `handleBackStep`'s `setViewStep(prev => ...)` reducer.
 *
 * The self path's pre-cert integers shift by `preCertOffset` (régimen + plan
 * inserted before situation). The new back-thread situation(2+off) -> plan(3) ->
 * régimen(2) -> who-setup(1) needs no explicit edges: with distinct integers the
 * default `prev - 1` arm threads it, and the same holds for the accountant-flow
 * owner (plan_gate 3 -> régimen 2 -> who-setup 1).
 */
export function backStep(ctx: BackContext): BackResult {
  const prev = ctx.prevViewStep;
  const st = ctx.ownerPostCertViewSteps;
  const off = ctx.preCertOffset;

  if (prev <= 0) return { step: prev };
  if (
    !ctx.isAccountantFlowOwner &&
    !ctx.isAccountantUser &&
    prev === 6 + off &&
    ctx.isAlreadyIssuer
  ) {
    return { step: 3 + off };
  }
  if (!ctx.isAccountantFlowOwner && !ctx.isAccountantUser && ctx.certBeforeDgi) {
    if (prev === 4 + off) return { step: 2 + off };
    if (prev === 3 + off) {
      return {
        step: ctx.certificateAcquisitionChoice === "fiscalo" ? 6 + off : 5 + off,
      };
    }
    if (prev === st.cae) return { step: 3 + off };
  }
  // scratch is DGI-first & linear (situación → dgi → certChoice → certContent →
  // [abitab, fiscalo only] → cae). The intermediate edges fall out of the
  // default `prev - 1` arm once the steps have distinct ascending integers; only
  // the cae → cert boundary needs an explicit edge, because manual skips the
  // Abitab step going forward (certContent → cae) and must skip it coming back
  // too. scratch = new-cert flow that is NOT cert-first, and not already_issuer.
  if (
    !ctx.isAccountantFlowOwner &&
    !ctx.isAccountantUser &&
    !ctx.certBeforeDgi &&
    !ctx.isAlreadyIssuer &&
    ctx.onboardingSituationChoice === "scratch" &&
    prev === st.cae
  ) {
    return {
      step: ctx.certificateAcquisitionChoice === "fiscalo" ? 6 + off : 5 + off,
    };
  }
  if (!ctx.isAccountantFlowOwner && !ctx.isAccountantUser) {
    if (
      ctx.isAlreadyIssuer &&
      (prev === st.literalE || prev === st.personalize)
    ) {
      return { step: st.fiscal };
    }
    if (prev === st.fiscal) {
      return {
        step: ctx.needsCfeImportStep && st.cfe != null ? st.cfe : st.cae,
      };
    }
    if (ctx.needsCfeImportStep && prev === st.cfe) {
      return { step: st.cae, cfeReset: true };
    }
  }
  return { step: prev - 1 };
}

/**
 * Input snapshot for the "Siguiente" disabled predicate. Distinct from
 * OnboardingStepContext because the predicate reads a larger, more transient set
 * (saving flags, the live cert-request, the benefit declaration).
 */
export interface CanAdvanceContext {
  needsPlanGate: boolean;
  needsRegimeGate: boolean;
  selectedPlanId: string | null;
  savingPlan: boolean;

  viewStep: number;
  /** Pre-DGI step insertion for the self path (2), 0 otherwise. Shifts the
   *  self situation/cert literals so they follow régimen + plan. */
  preCertOffset: number;
  situationChoice: "scratch" | "issuer_need_regularization" | "already_issuer" | null;
  certificateAcquisitionChoice: "myself" | "fiscalo" | null;
  certificateAcquisitionCanAdvance: boolean;

  isAccountantFlowOwner: boolean;
  isAccountantUser: boolean;
  certBeforeDgi: boolean;
  /** A fresh cert is being acquired (scratch OR regularization). Precomputed
   *  from the effective situation so it stays robust across reload. Gates the
   *  Abitab step on the cert request rather than on credentials. */
  newCertFlow: boolean;

  inviteStepCanProceed: boolean;
  accountantStepsComplete: boolean;
  savingLegalName: boolean;
  step6NotarialCanAdvance: boolean;

  certRequestStatus: string | null;
  certRequestIdDigitalRequestNumber: string | null;

  tenantStep6AbitabAcknowledgedAt: boolean;
  tenantCompanyType: string | null;
  tenantHasDgiCredentials: boolean;
  tenantPlanId: string | null;

  ownerPostCertViewSteps: OwnerPostCertViewSteps;
  caeListFetching: boolean;
  hasRequiredOnboardingCaes: boolean;
  needsCfeImportStep: boolean;
  cfeImportUploadCompleted: boolean;

  fiscalStepCanAdvance: boolean;
  savingFiscal: boolean;
  literalE: boolean | null;
  literalEProofFile: boolean;
  savingLiteralE: boolean;
  isMigrating: boolean;
  benefitDeclarationComplete: boolean;
}

/** Input snapshot for the "Siguiente" loading-spinner predicate. */
export interface NextLoadingContext {
  needsPlanGate: boolean;
  needsRegimeGate: boolean;
  savingPlan: boolean;
  viewStep: number;
  /** Pre-DGI step insertion for the self path (2), 0 otherwise. */
  preCertOffset: number;
  savingSituation: boolean;
  isAccountantFlowOwner: boolean;
  isAccountantUser: boolean;
  certificateStepNextLoading: boolean;
  savingFiscal: boolean;
  savingLiteralE: boolean;
  savingLegalName: boolean;
  caeListFetching: boolean;
  ownerPostCertViewSteps: OwnerPostCertViewSteps;
}

/** Pure form of the `nextButtonLoading` expression. */
export function nextButtonLoading(ctx: NextLoadingContext): boolean {
  const step = (n: number): boolean => ctx.viewStep === n + 1;
  const st = ctx.ownerPostCertViewSteps;
  const off = ctx.preCertOffset;

  if (ctx.needsRegimeGate || ctx.needsPlanGate) {
    return ctx.savingPlan;
  }

  return (
    (ctx.viewStep === 2 + off && ctx.savingSituation) ||
    (ctx.viewStep === 5 + off &&
      !ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.certificateStepNextLoading) ||
    (ctx.viewStep === 6 + off &&
      !ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.certificateStepNextLoading) ||
    (!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.viewStep === st.cae &&
      ctx.caeListFetching) ||
    (!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.viewStep === st.fiscal &&
      ctx.savingFiscal) ||
    (!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      st.literalE !== null &&
      ctx.viewStep === st.literalE &&
      ctx.savingLiteralE) ||
    (ctx.viewStep === 8 &&
      ctx.isAccountantFlowOwner &&
      (ctx.savingLiteralE || ctx.savingLegalName)) ||
    (!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.viewStep === st.personalize &&
      ctx.savingLegalName) ||
    (((!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      (ctx.viewStep === st.migrationOrPlanFirst ||
        ctx.viewStep === st.planMigrating)) ||
      (ctx.isAccountantFlowOwner && step(6))) &&
      ctx.savingPlan)
  );
}

/**
 * Pure form of the `nextButtonDisabled` expression. Returns true when the
 * "Siguiente" button must be disabled. `step(n)` in the source === viewStep === n+1.
 */
export function canAdvance(ctx: CanAdvanceContext): boolean {
  const step = (n: number): boolean => ctx.viewStep === n + 1;
  const off = ctx.preCertOffset;

  if (ctx.needsRegimeGate) {
    return !ctx.benefitDeclarationComplete;
  }

  if (ctx.needsPlanGate) {
    return !ctx.selectedPlanId || ctx.savingPlan;
  }

  const step5FiscaloCanAdvance =
    ctx.viewStep === 5 + off &&
    !ctx.isAccountantFlowOwner &&
    !ctx.isAccountantUser &&
    ctx.certificateAcquisitionChoice === "fiscalo" &&
    (ctx.certificateAcquisitionCanAdvance ||
      ctx.certRequestStatus === "pending" ||
      ctx.certRequestStatus === "in_progress" ||
      ctx.certRequestStatus === "failed");

  const st = ctx.ownerPostCertViewSteps;

  return (
    (ctx.viewStep === 2 + off && ctx.situationChoice === null) ||
    (ctx.viewStep === 4 + off &&
      !ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.certificateAcquisitionChoice === null) ||
    (ctx.viewStep === 5 + off &&
      !ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.certificateAcquisitionChoice !== "fiscalo" &&
      !ctx.certificateAcquisitionCanAdvance) ||
    (ctx.viewStep === 5 + off &&
      !ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.certificateAcquisitionChoice === "fiscalo" &&
      !step5FiscaloCanAdvance) ||
    (step(3) && ctx.isAccountantFlowOwner && !ctx.inviteStepCanProceed) ||
    (step(4) && ctx.isAccountantFlowOwner && !ctx.accountantStepsComplete) ||
    (step(5) && ctx.isAccountantFlowOwner && ctx.savingLegalName) ||
    (ctx.viewStep === 6 + off &&
      !ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      (() => {
        // New-cert flow (scratch OR regularization): the Abitab step advances
        // on the cert request, not on credentials (the .pfx arrives days later).
        // scratch is DGI-first so it is not certBeforeDgi, but it shares this
        // gate — key on newCertFlow, not the cert ordering.
        if (ctx.newCertFlow) {
          if (ctx.certRequestStatus === null) return true;
          if (ctx.certRequestStatus === "failed") return true;
          return false;
        }
        const step6AwaitingAbitab = ctx.tenantStep6AbitabAcknowledgedAt;
        const step6RequiresNotarial = ["sas", "sa", "srl"].includes(
          ctx.tenantCompanyType ?? "",
        );
        const step6EmailProcessed = Boolean(
          ctx.certRequestIdDigitalRequestNumber,
        );
        const step6ShowingNotarialSection =
          step6EmailProcessed && step6RequiresNotarial;
        // Advance on the cert REQUEST, not on the credentials — the same rule
        // the newCertFlow branch above already applies, and for the same
        // reason it gives: the .pfx arrives days later. Keying this on
        // credentials meant an already_issuer tenant who had been to Abitab
        // sat on a dead button until ID Digital delivered, while a scratch
        // tenant in the identical state could carry on.
        if (step6AwaitingAbitab) {
          return !isCertRequestInFlight(
            ctx.certRequestStatus as CertificateRequestStatus | null,
          );
        }
        if (step6ShowingNotarialSection) return !ctx.step6NotarialCanAdvance;
        return !ctx.tenantHasDgiCredentials;
      })()) ||
    (!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.viewStep === st.cae &&
      (ctx.caeListFetching || !ctx.hasRequiredOnboardingCaes)) ||
    (!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.needsCfeImportStep &&
      st.cfe != null &&
      ctx.viewStep === st.cfe &&
      !ctx.cfeImportUploadCompleted) ||
    (!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.viewStep === st.fiscal &&
      (!ctx.fiscalStepCanAdvance || ctx.savingFiscal)) ||
    (!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      st.literalE !== null &&
      ctx.viewStep === st.literalE &&
      (ctx.literalE === null ||
        ctx.savingLiteralE ||
        (ctx.literalE === true && !ctx.literalEProofFile))) ||
    (!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.viewStep === st.personalize &&
      ctx.savingLegalName) ||
    (step(6) && ctx.isAccountantUser && !ctx.tenantHasDgiCredentials) ||
    (step(6) &&
      ctx.isAccountantFlowOwner &&
      !ctx.tenantPlanId &&
      !ctx.selectedPlanId) ||
    (!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.viewStep === st.migrationOrPlanFirst &&
      !ctx.isMigrating &&
      !ctx.selectedPlanId) ||
    (!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      ctx.viewStep === st.planMigrating &&
      ctx.isMigrating &&
      !ctx.selectedPlanId) ||
    (!ctx.isAccountantFlowOwner &&
      !ctx.isAccountantUser &&
      (ctx.viewStep === st.migrationOrPlanFirst ||
        ctx.viewStep === st.planMigrating) &&
      ctx.savingPlan)
  );
}
